import type {
  ManagementTimelineGroup,
  ManagementTimelineMarker,
  ManagementTimelinePage,
  TraceArtifactContentPage,
} from '@internal/spassu-code-contract';
import type { CockpitMetricView } from '../model/cockpitViewModel';

export function mergePrimaryMetrics(
  current: CockpitMetricView[] | undefined,
  incoming: CockpitMetricView[],
): CockpitMetricView[] {
  if (!current) return incoming;
  const previous = new Map(current.map(metric => [metric.id, metric]));
  return incoming.map(metric => {
    if (!metric.primary || metric.status !== 'failed') return metric;
    const value = previous.get(metric.id);
    if (!value?.primary || value.rawValue === null) return metric;
    return {
      ...value,
      status: 'failed',
      stale: true,
      reasonCode: metric.reasonCode,
      unavailableReason: metric.unavailableReason,
      impact: metric.impact,
      action: metric.action,
    };
  });
}

export function mergeTimelinePages(
  current: ManagementTimelinePage | null,
  incoming: ManagementTimelinePage,
  reset: boolean,
): ManagementTimelinePage {
  if (reset || !current || incoming.page <= 1) return incoming;
  return {
    ...incoming,
    groups: uniqueByKey([...current.groups, ...incoming.groups]),
    markers: uniqueById([...current.markers, ...incoming.markers]),
  };
}

export function mergeArtifactPages(
  current: TraceArtifactContentPage | null,
  incoming: TraceArtifactContentPage,
): TraceArtifactContentPage {
  const offset = incoming.contentPage.offset;
  if (!current || offset === 0) return incoming;
  if (
    current.activityId !== incoming.activityId ||
    current.artifactId !== incoming.artifactId
  ) {
    return incoming;
  }
  const prefix = current.content.slice(0, offset);
  return {
    ...incoming,
    content: `${prefix}${incoming.content}`,
    contentPage: {
      ...incoming.contentPage,
      offset: 0,
      limit: prefix.length + incoming.content.length,
    },
  };
}

function uniqueByKey(
  groups: ManagementTimelineGroup[],
): ManagementTimelineGroup[] {
  return unique(groups, group => group.key);
}

function uniqueById(
  markers: ManagementTimelineMarker[],
): ManagementTimelineMarker[] {
  return unique(markers, marker => marker.id);
}

function unique<T>(items: T[], keyOf: (item: T) => string): T[] {
  const found = new Set<string>();
  return items.filter(item => {
    const key = keyOf(item);
    if (found.has(key)) return false;
    found.add(key);
    return true;
  });
}
